'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import PublicNavbar from '../landing/components/PublicNavbar';
import PublicFooter from '../landing/components/PublicFooter';

const team = [
  {
    name: 'Zac Munga',
    role: 'CEO & Co-Founder',
    bio: 'Visionary leader driving MuyuRide\'s mission to make parcel delivery safe, affordable, and community-powered across Kenya. Zac identified the gap between Kenya\'s millions of daily travelers and the need for affordable, trusted delivery — and built MuyuRide to bridge it.',
    initials: 'ZM',
    color: 'bg-[#0D9488]',
    fun: '🚀 Building the future of logistics, one trip at a time',
    image: '',
  },
  {
    name: 'Martha Wangui',
    role: 'HR & Head of Trust and Safety',
    bio: 'Oversees people operations and leads Trust & Safety — ensuring every traveler and sender on MuyuRide is verified, protected, and treated fairly. Martha designed the guarantor verification system that has kept MuyuRide\'s fraud rate at near-zero.',
    initials: 'MW',
    color: 'bg-[#F59E0B]',
    fun: '🛡️ Safety is not a feature — it\'s the foundation',
    image: '',
  },
  {
    name: 'Dorcas Wona Ndinya',
    role: 'Head of Growth',
    bio: 'Drives MuyuRide\'s expansion strategy, building the traveler and sender community across all 47 counties of Kenya. Dorcas led the expansion from 2 routes to 280+ active routes in under 3 years.',
    initials: 'DW',
    color: 'bg-green-600',
    fun: '📈 Growth is a team sport',
    image: '',
  },
  {
    name: 'Collins Kipkemboi Wekoye',
    role: 'Human Resources',
    bio: 'Manages human resources operations at MuyuRide — recruiting top talent, nurturing team culture, and ensuring every team member thrives. Collins has built a team that reflects the diversity of Kenya itself.',
    initials: 'CK',
    color: 'bg-[#7C3AED]',
    fun: '🤝 Great teams build great products',
    image: '',
  },
];

const values = [
  {
    title: 'Trust First, Always',
    desc: 'Every traveler is ID-verified, phone-verified, and has a guarantor on file. We don\'t cut corners on safety — ever. Your parcel is in real, accountable hands.',
    icon: '🛡️',
    bg: 'bg-teal-50 border-teal-100',
    accent: 'text-[#0D9488]',
  },
  {
    title: 'Fair for Everyone',
    desc: 'Senders get prices 60–80% cheaper than traditional couriers. Travelers earn KES 3,000–5,000 extra per trip. No hidden fees. No surprise charges. Ever.',
    icon: '⚖️',
    bg: 'bg-amber-50 border-amber-100',
    accent: 'text-[#D97706]',
  },
  {
    title: 'Built for Kenya',
    desc: 'We understand Kenyan roads, Kenyan culture, and Kenyan needs. M-Pesa first. Works on 3G. Covers all 47 counties. This isn\'t a Silicon Valley copy-paste — it\'s built from the ground up for Kenya.',
    icon: '🇰🇪',
    bg: 'bg-green-50 border-green-100',
    accent: 'text-green-700',
  },
  {
    title: 'Radical Transparency',
    desc: 'Escrow-protected payments, real-time GPS tracking, honest reviews, and clear dispute resolution. You always know exactly what\'s happening with your parcel.',
    icon: '🔍',
    bg: 'bg-blue-50 border-blue-100',
    accent: 'text-blue-700',
  },
  {
    title: 'Community-Powered',
    desc: 'MuyuRide works because Kenyans trust each other. Our guarantor system, review platform, and trust scores are built to amplify that existing community trust.',
    icon: '🤝',
    bg: 'bg-purple-50 border-purple-100',
    accent: 'text-purple-700',
  },
  {
    title: 'Relentlessly Improving',
    desc: 'We ship updates every week based on real user feedback. If something frustrates you, tell us — we\'ve probably already started fixing it.',
    icon: '🚀',
    bg: 'bg-rose-50 border-rose-100',
    accent: 'text-rose-600',
  },
];

const milestones = [
  { year: '2022', event: 'MuyuRide founded in Nairobi by Zac Munga. The insight: millions of Kenyans travel between cities every day with empty bag space, while millions of others need affordable, reliable delivery. Why not connect the two?', icon: '🌱' },
  { year: '2023 Q1', event: 'Beta launched with 50 hand-picked travelers on the Nairobi–Mombasa route. First 500 deliveries completed. Zero lost parcels. The guarantor verification system proved its worth immediately.', icon: '🚀' },
  { year: '2023 Q4', event: 'Expanded to 40 routes. Introduced M-Pesa escrow payments. Guarantor verification system launched platform-wide. Trust score algorithm deployed. Fraud rate dropped 94%.', icon: '🔐' },
  { year: '2024', event: 'Crossed 120 active routes. ID verification and admin review system launched. Reached 10,000 successful deliveries. Storage listings introduced. Student KYC program launched.', icon: '📦' },
  { year: '2025', event: 'Now live across all 47 counties. 280+ routes. 4,820+ verified travelers. 31,400+ deliveries. 98% on-time rate. Live GPS tracking launched. And we\'re just getting started.', icon: '🏆' },
];

const funFacts = [
  { stat: '98%', label: 'On-Time Rate', desc: 'Better than most airlines' },
  { stat: '0', label: 'Lost Parcels', desc: 'In our first 500 deliveries' },
  { stat: '47', label: 'Counties Covered', desc: 'Every single one' },
  { stat: '4.87★', label: 'Average Rating', desc: 'From 31,400+ reviews' },
  { stat: '60–80%', label: 'Cheaper', desc: 'Than traditional couriers' },
  { stat: '2 min', label: 'To Post an Order', desc: 'Seriously, we timed it' },
];

export default function AboutPage() {
  const [activeTeam, setActiveTeam] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <PublicNavbar />
      {/* ── HERO ── */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0D2B3E] to-[#0A1628]" />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-[#0D9488]/15 blur-[120px] animate-[pulse_6s_ease-in-out_infinite]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-[#F59E0B]/10 blur-[100px] animate-[pulse_8s_ease-in-out_infinite_2s]" />
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 space-y-6">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#0D9488]/15 border border-[#0D9488]/30 text-[#4DD9CC] text-sm font-bold uppercase tracking-widest">
                Our Story
              </span>
              <h1 className="text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.05]">
                We believe every<br />traveler is a courier.
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed">
                MuyuRide was born from a simple observation: millions of Kenyans travel between cities every day with empty bag space, while millions of others need affordable, reliable delivery.
              </p>
              <p className="text-lg text-slate-400 leading-relaxed">
                We connected the two — and built a system so trustworthy that 31,400+ deliveries later, we still haven&apos;t lost a single parcel.
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <Link href="/sign-up-login-screen"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#0D9488] text-white font-bold hover:bg-[#0F766E] transition-all duration-200 shadow-[0_2px_12px_rgba(13,148,136,0.3)]">
                  Join the Network →
                </Link>
                <Link href="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/10 border border-white/20 text-white font-semibold hover:bg-white/15 transition-all duration-200">
                  Talk to Us
                </Link>
              </div>
            </div>
            {/* Logo showcase */}
            <div className="flex-shrink-0 flex flex-col items-center gap-4">
              <div className="w-48 h-48 rounded-3xl bg-white/10 border border-white/20 backdrop-blur-sm flex items-center justify-center p-6 shadow-2xl">
                <Image
                  src="/assets/images/muyuride_logo.png"
                  alt="MuyuRide Logo — Kenya's Trusted Peer-to-Peer Travel Marketplace"
                  width={140}
                  height={140}
                  className="object-contain drop-shadow-lg"
                  priority
                />
              </div>
              <p className="text-slate-400 text-sm font-medium text-center">Kenya&apos;s Trusted P2P Marketplace</p>
              <p className="text-slate-500 text-xs font-medium italic text-center">&ldquo;Every Traveler is a Courier.&rdquo;</p>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#F8FAFC] to-transparent" />
      </section>

      {/* ── FUN FACTS BENTO ── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full bg-teal-50 border border-teal-200 text-[#0D9488] text-sm font-bold uppercase tracking-widest">By the Numbers</span>
            <h2 className="text-3xl font-extrabold text-[#0F172A] mt-3">The receipts don&apos;t lie.</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {funFacts?.map((fact, i) => (
              <div key={fact?.label}
                className={`rounded-2xl p-5 text-center border transition-all hover:-translate-y-1 hover:shadow-lg duration-300 ${
                  i === 0 ? 'bg-gradient-to-br from-teal-500/15 to-teal-500/5 border-teal-100' :
                  i === 2 ? 'bg-gradient-to-br from-amber-500/15 to-amber-500/5 border-amber-100' :
                  i === 4 ? 'bg-gradient-to-br from-green-500/15 to-green-500/5 border-green-100': 'bg-gradient-to-br from-slate-50 to-white border-slate-100'
                }`}>
                <div className="text-2xl font-extrabold text-[#0F172A] tabular-nums">{fact?.stat}</div>
                <div className="text-sm font-bold text-[#0F172A] mt-1">{fact?.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{fact?.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ORIGIN STORY ── */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <span className="inline-block px-4 py-1.5 rounded-full bg-teal-50 border border-teal-200 text-[#0D9488] text-sm font-bold uppercase tracking-widest">
                The Problem We Solved
              </span>
              <h2 className="text-4xl font-extrabold text-[#0F172A] tracking-tight leading-tight">
                KES 1,500 to send a laptop.<br />
                <span className="text-[#0D9488]">That&apos;s just wrong.</span>
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Traditional courier services charge KES 500–2,000 for a Nairobi–Mombasa delivery. Meanwhile, thousands of Kenyans make that exact journey every single day — with empty space in their bags.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                MuyuRide travelers do the same delivery for KES 200–600. Not because they&apos;re cutting corners — but because they&apos;re already going there. The trip is paid for. Your parcel is just along for the ride.
              </p>
              <div className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-teal-100 shadow-sm">
                <span className="text-3xl">💡</span>
                <div>
                  <p className="font-bold text-[#0F172A]">The insight that started it all</p>
                  <p className="text-sm text-slate-600 mt-1">If a bus goes from Nairobi to Kisumu anyway, why should a parcel cost more than a seat? It shouldn&apos;t. And now it doesn&apos;t. — <strong>Zac Munga, CEO</strong></p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { val: '2022', label: 'Founded in Nairobi', color: 'from-teal-500/15 to-teal-500/5 border-teal-100', icon: '🌱' },
                { val: '280+', label: 'Active Routes', color: 'from-amber-500/15 to-amber-500/5 border-amber-100', icon: '🗺️' },
                { val: '4,820+', label: 'Verified Travelers', color: 'from-blue-500/15 to-blue-500/5 border-blue-100', icon: '🛡️' },
                { val: '98%', label: 'On-Time Rate', color: 'from-green-500/15 to-green-500/5 border-green-100', icon: '⏱️' },
              ]?.map((item) => (
                <div key={item?.label} className={`rounded-2xl p-6 bg-gradient-to-br ${item?.color} border text-center hover:-translate-y-1 transition-transform duration-300`}>
                  <div className="text-3xl mb-2">{item?.icon}</div>
                  <div className="text-3xl font-extrabold text-[#0F172A] tabular-nums">{item?.val}</div>
                  <div className="text-sm text-slate-500 mt-1">{item?.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── VALUES ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-14">
            <span className="inline-block px-4 py-1.5 rounded-full bg-teal-50 border border-teal-200 text-[#0D9488] text-sm font-bold uppercase tracking-widest">
              What We Stand For
            </span>
            <h2 className="text-4xl font-extrabold text-[#0F172A] tracking-tight">Six things we won&apos;t compromise on.</h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">These aren&apos;t just words on a wall. They&apos;re the decisions we make every day when building features, resolving disputes, and hiring people.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {values?.map((v) => (
              <div key={v?.title} className={`rounded-2xl p-7 border ${v?.bg} hover:-translate-y-1 hover:shadow-lg transition-all duration-300`}>
                <div className="text-4xl mb-4">{v?.icon}</div>
                <h3 className={`text-lg font-extrabold mb-2 ${v?.accent}`}>{v?.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{v?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TIMELINE ── */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-14">
            <span className="inline-block px-4 py-1.5 rounded-full bg-teal-50 border border-teal-200 text-[#0D9488] text-sm font-bold uppercase tracking-widest">
              Our Journey
            </span>
            <h2 className="text-4xl font-extrabold text-[#0F172A] tracking-tight">From idea to 47 counties.</h2>
          </div>
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-[#0D9488] via-[#F59E0B] to-[#16A34A]" />
            <div className="space-y-8">
              {milestones?.map((m) => (
                <div key={m?.year} className="relative flex gap-6 pl-20">
                  <div className="absolute left-4 top-1 w-8 h-8 rounded-full bg-white border-2 border-[#0D9488] flex items-center justify-center text-base shadow-sm">
                    {m?.icon}
                  </div>
                  <div className="flex-1 rounded-2xl p-5 bg-white border border-slate-100 hover:border-teal-200 hover:shadow-md transition-all duration-300">
                    <div className="text-sm font-extrabold text-[#0D9488] mb-2">{m?.year}</div>
                    <p className="text-sm text-slate-600 leading-relaxed">{m?.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── TEAM ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-14">
            <span className="inline-block px-4 py-1.5 rounded-full bg-teal-50 border border-teal-200 text-[#0D9488] text-sm font-bold uppercase tracking-widest">
              The Team
            </span>
            <h2 className="text-4xl font-extrabold text-[#0F172A] tracking-tight">The people behind MuyuRide.</h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">A small, passionate team of Kenyans building the logistics infrastructure their country deserves.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {team?.map((member) => (
              <div
                key={member?.name}
                className={`rounded-2xl p-6 border transition-all duration-300 cursor-pointer ${
                  activeTeam === member?.name
                    ? 'border-[#0D9488] bg-teal-50 shadow-lg -translate-y-1'
                    : 'border-slate-100 bg-white hover:border-teal-200 hover:shadow-md hover:-translate-y-0.5'
                }`}
                onClick={() => setActiveTeam(activeTeam === member?.name ? null : member?.name)}
              >
                <div className={`w-16 h-16 rounded-2xl ${member?.color} flex items-center justify-center text-white text-xl font-extrabold mb-4 shadow-sm`}>
                  {member?.initials}
                </div>
                <h3 className="font-extrabold text-[#0F172A] text-base">{member?.name}</h3>
                <p className="text-xs font-bold text-[#0D9488] mt-0.5 mb-3">{member?.role}</p>
                <p className="text-xs text-slate-600 leading-relaxed">{member?.bio}</p>
                {activeTeam === member?.name && (
                  <div className="mt-4 pt-4 border-t border-teal-200">
                    <p className="text-xs text-[#0D9488] font-semibold italic">{member?.fun}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MISSION STATEMENT ── */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] to-[#0D2B3E] relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-[#0D9488]/10 blur-[100px]" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-[#F59E0B]/8 blur-[100px]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <div className="flex justify-center">
            <div className="w-24 h-24 rounded-3xl bg-white/10 border border-white/20 flex items-center justify-center p-4 backdrop-blur-sm">
              <Image
                src="/assets/images/muyuride_logo.png"
                alt="MuyuRide Logo"
                width={64}
                height={64}
                className="object-contain"
              />
            </div>
          </div>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
            Our mission is simple.
          </h2>
          <p className="text-xl text-slate-300 leading-relaxed max-w-2xl mx-auto">
            Make parcel delivery in Kenya as easy, affordable, and trustworthy as sending a WhatsApp message. Every Kenyan should be able to send anything, anywhere, without fear of loss, fraud, or overcharging.
          </p>
          <p className="text-lg text-[#4DD9CC] font-bold italic">
            &ldquo;Every Traveler is a Courier. Every Route is a Delivery.&rdquo;
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Link href="/sign-up-login-screen"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-[#0D9488] text-white font-extrabold hover:bg-[#0F766E] transition-all duration-200 shadow-[0_4px_20px_rgba(13,148,136,0.4)] hover:-translate-y-0.5">
              Join MuyuRide Today →
            </Link>
            <Link href="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-white/10 border border-white/20 text-white font-semibold hover:bg-white/15 transition-all duration-200">
              Contact the Team
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
